package dev.danko.roblox_craft_ai.uikit.components

import dev.danko.roblox_craft_ai.uikit.i18n.strings
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.ProjectFile
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.deg
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

private fun getFileIcon(path: String): Pair<String, Color> {
    return when {
        path.endsWith(".server.luau") || path.endsWith(".server.lua") -> "⚙️" to Color.rgb(0xF59E0B)
        path.endsWith(".client.luau") || path.endsWith(".client.lua") -> "🖥️" to Color.rgb(0x3B82F6)
        path.endsWith(".luau") || path.endsWith(".lua") -> "📦" to Color.rgb(0x10B981)
        path.endsWith(".json") -> "⚙️" to Color.rgb(0xA855F7)
        path.endsWith(".toml") -> "📄" to Color.rgb(0xEC4899)
        path.endsWith(".md") -> "📝" to Color.rgb(0x64748B)
        else -> "📄" to Color.rgb(0x94A3B8)
    }
}

@Composable
fun FileTreeViewer(
    files: List<ProjectFile>,
    selectedPath: String?,
    onFileSelected: (ProjectFile) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalAppTheme.current
    val s = strings()
    val palette = theme.colors
    var searchQuery by remember { mutableStateOf("") }

    val filteredFiles = remember(files, searchQuery) {
        if (searchQuery.isBlank()) files
        else files.filter { it.path.contains(searchQuery, ignoreCase = true) }
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(palette.surface)
            .borderRadius(theme.radii.md)
            .border(1.px, LineStyle.Solid, palette.border)
            .padding(0.5.cssRem)
            .then(modifier)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 0.5.cssRem, topBottom = 0.35.cssRem)
                .margin(bottom = 0.5.cssRem)
                .borderBottom(1.px, LineStyle.Solid, palette.border),
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = s.code.projectTree(files.size),
                modifier = Modifier
                    .weight(1f)
                    .fontSize(0.8.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .color(palette.textPrimary)
            )

            AppInput(
                value = searchQuery,
                onValueChanged = { searchQuery = it },
                placeholder = s.code.filterPlaceholder,
                modifier = Modifier.width(130.px)
            )
        }

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxHeight(22.cssRem)
                .overflow(Overflow.Auto)
                .gap(2.px)
        ) {
            filteredFiles.forEach { file ->
                val isSelected = file.path == selectedPath
                var isHovered by remember { mutableStateOf(false) }
                val (iconEmoji, iconColor) = getFileIcon(file.path)

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 0.65.cssRem, topBottom = 0.35.cssRem)
                        .borderRadius(theme.radii.sm)
                        .backgroundColor(
                            when {
                                isSelected -> palette.accent.toRgb().copyf(alpha = 0.2f)
                                isHovered -> palette.surfaceElevated
                                else -> Color.rgba(0, 0, 0, 0f)
                            }
                        )
                        .border(
                            1.px,
                            LineStyle.Solid,
                            if (isSelected) palette.accent else Color.rgba(0, 0, 0, 0f)
                        )
                        .cursor(Cursor.Pointer)
                        .onClick { onFileSelected(file) }
                        .onMouseEnter { isHovered = true }
                        .onMouseLeave { isHovered = false }
                        .transition(Transition.of("all", 150.ms)),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        text = iconEmoji,
                        modifier = Modifier.fontSize(0.85.cssRem).margin(right = 0.45.cssRem)
                    )

                    SpanText(
                        text = file.path,
                        modifier = Modifier
                            .weight(1f)
                            .fontSize(0.8.cssRem)
                            .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "monospace")
                            .fontWeight(if (isSelected) FontWeight.Bold else FontWeight.Normal)
                            .color(if (isSelected) palette.accent else palette.textPrimary)
                    )

                    if (file.sizeBytes > 0) {
                        SpanText(
                            text = "${file.sizeBytes} B",
                            modifier = Modifier
                                .fontSize(0.7.cssRem)
                                .color(palette.textSecondary)
                        )
                    }
                }
            }
        }
    }
}
